import React, { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, MapPin, Headphones, Mountain, Compass, ArrowLeft } from "lucide-react";
import { TRAILS, IMAGES } from "../data/content";
import AudioPlayer from "../components/AudioPlayer";
import TrailMap from "../components/TrailMap";
import { WaveformDecor } from "../components/Waveform";

export default function TrailDetail() {
  const { slug } = useParams();
  const trail = TRAILS[slug];
  const [activeStop, setActiveStop] = useState(1);

  if (!trail) return <Navigate to="/featured-trails" replace />;

  const stop = trail.stopList.find((s) => s.n === activeStop) || trail.stopList[0];

  return (
    <div data-testid={`page-trail-${trail.slug}`}>
      {/* HERO */}
      <section className="relative pt-32 pb-20 md:pt-44 md:pb-32 overflow-hidden bg-brand-ink text-brand-cream min-h-[80vh] flex items-end">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${trail.hero})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-brand-ink/90" />
        <WaveformDecor className="absolute bottom-6 left-0 right-0 h-20 opacity-40" color="#F7F5F0" opacity={0.6} />

        {trail.badge && (
          <img
            src={trail.badge}
            alt={`${trail.shortName || trail.name} badge`}
            className="hidden md:block absolute top-32 right-8 lg:top-36 lg:right-16 w-36 lg:w-44 z-10 drop-shadow-2xl rotate-[6deg]"
          />
        )}

        <div className="container-px relative z-10 w-full">
          <Link
            to="/featured-trails"
            data-testid="back-to-trails"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-brand-cream/80 hover:text-brand-ochre"
          >
            <ArrowLeft size={14} /> All trails
          </Link>
          <div className="mt-6 overline text-brand-ochre">{trail.region}</div>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-8xl mt-4 leading-[1.02] max-w-4xl">
            {trail.shortName || trail.name}{trail.name.includes("Talking Trails") && (<><br /><span className="italic font-light">Talking Trails</span></>)}
          </h1>

          <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-4 max-w-2xl">
            {[
              { Icon: Headphones, k: trail.stops, l: "Listening Stops" },
              { Icon: Compass, k: trail.duration, l: "Walk Time" },
              { Icon: MapPin, k: trail.region.split(",")[0], l: "Country" },
            ].map(({ Icon, k, l }) => (
              <div key={l} className="border-t border-brand-cream/20 pt-4">
                <Icon size={14} className="text-brand-ochre" />
                <div className="font-serif text-2xl mt-1.5 text-brand-cream">{k}</div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-brand-cream/60 mt-1">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="py-20 md:py-28">
        <div className="container-px grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline">Overview</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight text-brand-charcoal">
              Walk slowly.<br /><span className="italic font-light">Listen carefully.</span>
            </h2>
            <div className="hairline mt-8" />
            <div className="mt-6 flex flex-wrap gap-2">
              {trail.themes.map((th) => (
                <span
                  key={th}
                  className="text-[11px] uppercase tracking-[0.18em] border border-brand-charcoal/20 px-3 py-1.5 text-brand-charcoal/80"
                >
                  {th}
                </span>
              ))}
            </div>
          </div>
          <div className="lg:col-span-7 text-brand-muted text-lg font-light leading-relaxed">
            {trail.badge && (
              <img
                src={trail.badge}
                alt={`${trail.shortName || trail.name} badge`}
                className="md:hidden mb-6 w-32 drop-shadow-lg"
              />
            )}
            <p>{trail.intro}</p>
            <p className="mt-5">
              Each stop along this trail unlocks a layer of place — a voice, a memory,
              a sound, a fragment of Country. Stand where the story belongs and let it
              shape what you see.
            </p>
          </div>
        </div>
      </section>

      {/* MAP + STOPS */}
      <section className="bg-brand-sand py-20 md:py-28">
        <div className="container-px">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
            <div>
              <div className="overline">The Trail</div>
              <h2 className="font-serif text-4xl md:text-5xl mt-3 leading-tight text-brand-charcoal max-w-2xl">
                {trail.stops} listening stops, woven into the landscape.
              </h2>
            </div>
            <p className="text-brand-muted font-light max-w-md">
              Tap a marker on the map, or scroll the list below. Each stop carries a
              short audio story to be heard exactly where it belongs.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-7">
              <TrailMap stops={trail.stopList} image={trail.secondary} />
            </div>
            <div className="lg:col-span-5">
              <div className="border border-brand-line/70 bg-brand-cream max-h-[520px] overflow-y-auto divide-y divide-brand-line/60">
                {trail.stopList.map((s) => (
                  <button
                    key={s.n}
                    data-testid={`stop-item-${s.n}`}
                    onClick={() => setActiveStop(s.n)}
                    className={`w-full text-left px-6 py-5 flex items-center gap-5 transition-colors ${
                      activeStop === s.n ? "bg-brand-sand" : "hover:bg-brand-sand/60"
                    }`}
                  >
                    <span
                      className={`font-serif text-2xl w-10 ${
                        activeStop === s.n ? "text-brand-ochre" : "text-brand-charcoal/40"
                      }`}
                    >
                      {String(s.n).padStart(2, "0")}
                    </span>
                    <span className="flex-1">
                      <span className="block text-brand-charcoal font-medium">{s.title}</span>
                      <span className="block text-xs text-brand-muted italic">{s.subtitle}</span>
                    </span>
                    <span className="text-[11px] uppercase tracking-[0.18em] text-brand-muted">{s.duration}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AUDIO PLAYER FEATURE */}
      <section className="py-20 md:py-28">
        <div className="container-px grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <div className="overline">Now playing</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-3 leading-tight text-brand-charcoal">
              Stop {String(stop.n).padStart(2, "0")}<br />
              <span className="italic font-light">{stop.title}</span>
            </h2>
            <p className="mt-5 text-brand-muted text-lg font-light leading-relaxed">
              A sample of the audio storytelling that brings {trail.shortName || trail.name} to life.
              In the full experience, this is hosted on-trail via QR markers and signage.
            </p>
            <div className="mt-8 flex items-center gap-4 text-xs uppercase tracking-[0.22em] text-brand-charcoal/70">
              <span className="flex items-center gap-2"><Headphones size={14} className="text-brand-ochre" /> {stop.duration}</span>
              <span>·</span>
              <span>{trail.region}</span>
            </div>
          </div>

          <div className="lg:col-span-7">
            <AudioPlayer stop={stop} index={stop.n} src={trail.sampleAudio} />
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section className="bg-brand-ink py-20 md:py-28 text-brand-cream">
        <div className="container-px grid grid-cols-1 md:grid-cols-12 gap-4">
          <div
            className="md:col-span-8 aspect-[4/3] bg-cover bg-center"
            style={{ backgroundImage: `url(${(trail.gallery && trail.gallery[0]) || trail.hero})` }}
          />
          <div
            className="md:col-span-4 aspect-[4/5] bg-cover bg-center"
            style={{ backgroundImage: `url(${(trail.gallery && trail.gallery[1]) || trail.tertiary})` }}
          />
          <div
            className="md:col-span-5 aspect-[4/3] bg-cover bg-center"
            style={{ backgroundImage: `url(${(trail.gallery && trail.gallery[2]) || trail.secondary})` }}
          />
          <div className="md:col-span-7 aspect-[4/3] bg-brand-ink p-10 md:p-14 flex flex-col justify-between border border-brand-cream/15">
            <div>
              <div className="overline text-brand-ochre">Continue listening</div>
              <h3 className="font-serif text-3xl md:text-4xl mt-3 leading-tight text-brand-cream max-w-md">
                Bring the Stories of Your Place to Life.
              </h3>
              <p className="mt-4 text-brand-cream/70 font-light leading-relaxed max-w-md">
                We work with parks, councils, museums, Traditional Owners and tourism
                operators to develop trails rooted in voice, story and Country.
              </p>
            </div>
            <Link
              to="/contact"
              data-testid="trail-detail-cta"
              className="self-start mt-6 inline-flex items-center gap-2 bg-brand-ochre text-brand-cream px-7 py-3.5 text-xs uppercase tracking-[0.22em] hover:bg-brand-ochreDark transition-colors"
            >
              Start a Conversation <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
